/**
 * Cookie Utility Functions
 *
 * SECURITY FIXES:
 * - C-1/H-8: Added 8-hour default expiration for session timeout
 * - C-2: Added Secure and SameSite=Strict flags (HttpOnly not possible client-side)
 * - H-1: Fixed ReDoS vulnerability in getCookie()
 *
 * NOTE: Server SHOULD set cookies with HttpOnly flag via Set-Cookie header.
 * Client-side cookies cannot be HttpOnly and are vulnerable to XSS.
 */

/**
 * Set a cookie with security flags and expiration.
 *
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number|null} expiresMs - Expiration in milliseconds (defaults to 8 hours)
 */
export function setCookie(name, value, expiresMs = null) {
  const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;
  const expirationTime = expiresMs !== null ? expiresMs : EIGHT_HOURS_MS;
  const expirationDate = new Date(Date.now() + expirationTime);

  let cookie = `${name}=${encodeURIComponent(value)}; path=/;`;
  cookie += ` expires=${expirationDate.toUTCString()};`;
  cookie += ` max-age=${Math.floor(expirationTime / 1000)};`;
  cookie += ` SameSite=Strict;`;

  // Add Secure flag in production (HTTPS)
  if (window.location.protocol === "https:") {
    cookie += ` Secure;`;
  }

  document.cookie = cookie;
}

export function eraseCookie(name) {
  document.cookie = `${name}=; Max-Age=0; path=/;`;
}

/**
 * Get a cookie value by name.
 *
 * SECURITY FIX H-1: Fixed ReDoS vulnerability by using safer parsing.
 * Previous regex was vulnerable to catastrophic backtracking.
 *
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null
 */
export function getCookie(name) {
  // Safer parsing without regex to avoid ReDoS
  const cookies = document.cookie.split("; ");
  for (const cookie of cookies) {
    const [key, value] = cookie.split("=");
    if (key === name) {
      return value ? decodeURIComponent(value) : null;
    }
  }
  return null;
}

/**
 * Fallback: set auth cookies from login response ONLY when the server
 * has not already set them via Set-Cookie header (which includes HttpOnly).
 *
 * SECURITY FIX C-1: Cookies now set with 8-hour expiration (default in setCookie).
 * SECURITY FIX C-2: Cookies set with Secure and SameSite=Strict flags.
 * SECURITY FIX H-1: Skip client-side cookie write when the server already set the
 *   cookie (HttpOnly cookies are invisible to JS, so we check for the non-HttpOnly
 *   fallback). Once the backend always sends Set-Cookie with HttpOnly, this
 *   function can be removed entirely.
 *
 * TODO(backend): Migrate login API to return auth tokens via Set-Cookie headers
 *   with HttpOnly; Secure; SameSite=Strict flags. Once confirmed, remove the
 *   client-side setCookie calls below.
 */
export function ensureAuthCookiesFromResponse(response) {
  if (!response) return;

  // Only set client-side if the cookie is not already present
  // (server Set-Cookie with HttpOnly would not be readable here,
  //  but the cookie would already be sent on subsequent requests)
  if (response.session_id && !getCookie("session_id")) {
    setCookie("session_id", response.session_id);
  }
  if (response.manager_token && !getCookie("manager_token")) {
    setCookie("manager_token", response.manager_token);
  }
  if (response.system_id && !getCookie("system_id")) {
    setCookie("system_id", response.system_id);
  }
}

export function eraseAuthCookies() {
  eraseCookie("session_id");
  eraseCookie("manager_token");
  eraseCookie("system_id");
}
